import { useNavigate, Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Building2, Plus } from "lucide-react";

export function SelectOrgPage() {
  const navigate = useNavigate();
  usePageTitle("Select Organization");
  const { data: orgs, isPending } = authClient.useListOrganizations();

  function handleSelect(org: { id: string; slug: string }) {
    navigate(`/${org.id}/${org.slug}/dashboard`, { replace: true });
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!orgs || orgs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No organizations</CardTitle>
          <CardDescription>
            You&apos;re not part of any organization yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/onboarding/create-org">
            <Button className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              Create your first organization
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select an organization</CardTitle>
        <CardDescription>
          Choose which organization to work in
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {orgs.map((org) => (
          <button
            key={org.id}
            onClick={() => handleSelect(org)}
            className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">{org.name}</p>
              <p className="text-sm text-muted-foreground">{org.slug}</p>
            </div>
          </button>
        ))}
        <div className="pt-2 text-center">
          <Link
            to="/onboarding/create-org"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            <Plus className="mr-1 inline h-3 w-3" />
            Create a new organization
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
